import type { EventSummary } from '@finder/contracts';
import type { JSX } from 'react';
import { Icon } from './Icon.js';
import { confidenceCue, hasOfferSplit, showsDrink, showsFood } from './format.js';

/**
 * Food and drink badges. Unconfirmed or Confirmed is said once, on the Food
 * badge (or the combined food-and-drinks pill). Drinks does not repeat it.
 * The longer explanation lives once under the result count, not on the badge.
 */
export function OfferBadges({
  event,
}: {
  event: Pick<EventSummary, 'offers' | 'foodState'>;
}): JSX.Element {
  const cue = confidenceCue(event.foodState);
  const splitKnown = hasOfferSplit(event);
  const food = showsFood(event);
  const drink = showsDrink(event);
  return (
    <div className="offers">
      <span className="pill pill--entry">
        <Icon name="ticket" size={15} />
        Free entry
      </span>
      {splitKnown ? (
        <>
          {food ? (
            <span className="pill pill--food">
              Food
              <Icon name="utensils" size={15} />
              <ConfidenceCueTag cue={cue} />
            </span>
          ) : null}
          {drink ? (
            <span className="pill pill--drink">
              Drinks
              <Icon name="cup" size={15} />
            </span>
          ) : null}
        </>
      ) : (
        <span className="pill pill--food">
          Free food &amp; drinks
          <Icon name="utensils" size={15} />
          <ConfidenceCueTag cue={cue} />
        </span>
      )}
    </div>
  );
}

function ConfidenceCueTag({ cue }: { cue: ReturnType<typeof confidenceCue> }): JSX.Element {
  return (
    <span className={`conf conf--${cue.tone}`} title={cue.description}>
      {cue.tone === 'confirmed' ? (
        <Icon name="check" size={12} strokeWidth={3} />
      ) : (
        <span className="conf-dot" aria-hidden="true" />
      )}
      {cue.label}
    </span>
  );
}
